import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  Button,
  ButtonIcon,
  Box,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Flex,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { Focusable } from 'react-aria-components';
import {
  RiCheckLine,
  RiDeleteBinLine,
  RiDownloadLine,
  RiEditLine,
  RiEyeLine,
  RiFileCopyLine,
  RiMore2Line,
} from '@remixicon/react';
import type { CheckDefinition } from '@internal/backstage-plugin-pulse-common';
import {
  pulseCheckUpdatePermission,
  pulseCheckDeletePermission,
  toPulseCheckDefinitionResourceRef,
} from '@internal/backstage-plugin-pulse-common';
import { useDeleteCheck } from '../../hooks';
import { checksEditSubRouteRef } from '../../routes';
import { checkToYaml, downloadCheckYaml } from './check-yaml';
import { CheckPreviewBody, checkToPreviewProps } from './check-preview-card';

type CheckActionsButtonProps = {
  check: CheckDefinition;
  onDeleted?: () => void;
};

// Matches BUI's disabled button treatment (opacity 0.5 + not-allowed cursor).
const disabledItemStyle = { opacity: 0.5, cursor: 'not-allowed' as const };

/**
 * Self-contained kebab action button for a check, mirroring the catalog entity
 * page actions menu. View config and export are available to everyone; edit and
 * delete are shown but disabled with a tooltip when the check is config-managed
 * or the user lacks the backend permission for that check.
 */
export const CheckActionsButton = ({
  check,
  onDeleted,
}: CheckActionsButtonProps) => {
  const navigate = useNavigate();
  const getEditPath = useRouteRef(checksEditSubRouteRef);
  const { remove } = useDeleteCheck();

  const [viewing, setViewing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const copyConfig = async () => {
    try {
      await navigator.clipboard.writeText(checkToYaml(check));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const isConfig = check.source !== 'db';
  const { allowed: canEdit } = usePermission({
    permission: pulseCheckUpdatePermission,
    resourceRef: toPulseCheckDefinitionResourceRef(check.id),
  });
  const { allowed: canDelete } = usePermission({
    permission: pulseCheckDeletePermission,
    resourceRef: toPulseCheckDefinitionResourceRef(check.id),
  });

  const configReason =
    'This check is defined in YAML and cannot be edited or deleted here';
  const permissionReason =
    'Only the check owner or an admin can edit or delete this check';
  const modifyReason = (allowed: boolean | undefined) => {
    if (isConfig) {
      return configReason;
    }
    if (!allowed) {
      return permissionReason;
    }
    return undefined;
  };
  const editReason = modifyReason(canEdit);
  const deleteReason = modifyReason(canDelete);
  const disabledKeys = [
    ...(editReason ? ['edit'] : []),
    ...(deleteReason ? ['delete'] : []),
  ];

  const confirmDelete = async () => {
    try {
      await remove(check.id);
      setDeleting(false);
      onDeleted?.();
    } catch (error_) {
      setDeleteError((error_ as Error).message);
    }
  };

  const actionContent = (icon: ReactElement, text: string, reason?: string) =>
    reason ? (
      <TooltipTrigger delay={0}>
        <Focusable>
          {/* Icon + text share the trigger so the whole row (incl. icon) shows the tip. */}
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--bui-space-2)',
              flexGrow: 1,
              alignSelf: 'stretch',
            }}
          >
            {icon}
            {text}
          </span>
        </Focusable>
        <Tooltip placement="left">{reason}</Tooltip>
      </TooltipTrigger>
    ) : (
      text
    );

  return (
    <>
      <MenuTrigger>
        <ButtonIcon
          icon={<RiMore2Line />}
          aria-label="Check actions"
          variant="secondary"
        />
        <Menu placement="bottom end" disabledKeys={disabledKeys}>
          <MenuSection title={isConfig ? 'YAML configured' : 'UI configured'}>
            <MenuItem
              id="export"
              iconStart={<RiDownloadLine />}
              onAction={() => downloadCheckYaml(check)}
            >
              Export
            </MenuItem>
            <MenuItem
              id="view"
              iconStart={<RiEyeLine />}
              onAction={() => setViewing(true)}
            >
              View config
            </MenuItem>
            <MenuItem
              id="edit"
              iconStart={editReason ? undefined : <RiEditLine />}
              style={editReason ? disabledItemStyle : undefined}
              onAction={() => navigate(getEditPath?.({ id: check.id }) ?? '')}
            >
              {actionContent(<RiEditLine size={16} />, 'Edit', editReason)}
            </MenuItem>
            <MenuItem
              id="delete"
              iconStart={deleteReason ? undefined : <RiDeleteBinLine />}
              color="danger"
              style={deleteReason ? disabledItemStyle : undefined}
              onAction={() => {
                setDeleteError(null);
                setDeleting(true);
              }}
            >
              {actionContent(
                <RiDeleteBinLine size={16} />,
                'Delete',
                deleteReason,
              )}
            </MenuItem>
          </MenuSection>
        </Menu>
      </MenuTrigger>

      <Dialog
        isOpen={viewing}
        onOpenChange={value => {
          if (!value) {
            setViewing(false);
          }
        }}
        width={640}
      >
        <DialogHeader>{`Config: ${check.name}`}</DialogHeader>
        <DialogBody>
          <Tabs>
            <TabList>
              <Tab id="overview">Overview</Tab>
              <Tab id="yaml">YAML</Tab>
            </TabList>
            <TabPanel id="overview">
              <Box>
                <CheckPreviewBody {...checkToPreviewProps(check)} />
              </Box>
            </TabPanel>
            <TabPanel id="yaml">
              <Box position="relative">
                <Box
                  position="absolute"
                  style={{
                    top: 'var(--bui-space-1)',
                    right: 'var(--bui-space-1)',
                    zIndex: 1,
                  }}
                >
                  <ButtonIcon
                    icon={copied ? <RiCheckLine /> : <RiFileCopyLine />}
                    aria-label="Copy config"
                    variant="tertiary"
                    size="small"
                    onPress={copyConfig}
                  />
                </Box>
                <pre
                  style={{
                    margin: 0,
                    maxHeight: '60vh',
                    overflow: 'auto',
                    fontSize: '0.8125rem',
                    whiteSpace: 'pre',
                    background: 'var(--bui-bg-neutral-2)',
                    borderRadius: 'var(--bui-radius-2)',
                    padding: 'var(--bui-space-2)',
                  }}
                >
                  {checkToYaml(check)}
                </pre>
              </Box>
            </TabPanel>
          </Tabs>
        </DialogBody>
        <DialogFooter>
          <Flex justify="end" gap="2">
            <Button
              variant="secondary"
              iconStart={<RiDownloadLine />}
              onClick={() => downloadCheckYaml(check)}
            >
              Export
            </Button>
            <Button variant="primary" onClick={() => setViewing(false)}>
              Close
            </Button>
          </Flex>
        </DialogFooter>
      </Dialog>

      <Dialog
        isOpen={deleting}
        onOpenChange={value => {
          if (!value) {
            setDeleting(false);
          }
        }}
        width={480}
      >
        <DialogHeader>Delete check</DialogHeader>
        <DialogBody>
          <Flex direction="column" gap="2">
            <Text variant="body-medium" color="secondary">
              {`Delete check "${check.name}"? This cannot be undone.`}
            </Text>
            {deleteError ? (
              <Text variant="body-small" color="danger">
                {deleteError}
              </Text>
            ) : null}
          </Flex>
        </DialogBody>
        <DialogFooter>
          <Flex justify="end" gap="2">
            <Button variant="secondary" onClick={() => setDeleting(false)}>
              Cancel
            </Button>
            <Button variant="primary" destructive onClick={confirmDelete}>
              Delete
            </Button>
          </Flex>
        </DialogFooter>
      </Dialog>
    </>
  );
};
